"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { MoveUpRight } from "lucide-react";
import { useTranslations } from "next-intl";

export const AIPhotos = () => {
  const t = useTranslations('aiPhotos');
  const [activeCard, setActiveCard] = useState(0);
  const [particles, setParticles] = useState<Array<{top: number, left: number, duration: number, delay: number}>>([]);
  
  useEffect(() => {
    const newParticles = [...Array(20)].map(() => ({
      top: Math.random() * 100,
      left: Math.random() * 100,
      duration: 3 + Math.random() * 5,
      delay: Math.random() * 5
    }));
    setParticles(newParticles);
  }, []);
  
  // Auto-rotate cards every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCard((current) => (current + 1) % 3);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <section id="examples" className="relative w-full bg-background py-12 md:py-24 overflow-hidden">
      {/* Background gradient */}
      <Image
        src="/images/gradiant-features.webp"
        alt="Background gradient"
        fill
        className="pointer-events-none"
        priority
      />
      
      {/* Animated particles */}
      <div className="absolute inset-0 opacity-30">
        {particles.map((particle, i) => (
          <div 
            key={i} 
            className="absolute w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-white opacity-60"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
              animation: `float ${particle.duration}s infinite ease-in-out ${particle.delay}s`,
            }}
          />
        ))}
      </div>

      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(0.8); opacity: 0.2; }
          50% { transform: translateY(-20px) scale(1); opacity: 0.6; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
        {/* Left side - Image Gallery */}
        <div className="relative w-full lg:w-1/2 h-[350px] sm:h-[400px] md:h-[450px] lg:h-[500px]">
          {/* Card showcase container */}
          <div className="absolute w-full h-full left-0 top-0 flex items-center justify-center">
            {/* Professional Photo */}
            <div 
              className={`absolute w-[200px] h-[280px] sm:w-[220px] sm:h-[300px] md:w-[240px] md:h-[320px] lg:w-[260px] lg:h-[350px] 
                        transition-all duration-700 ease-in-out rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden
                        ${activeCard === 0 
                          ? 'opacity-100 z-30 translate-x-0 scale-100' 
                          : activeCard === 1 
                            ? 'opacity-60 z-20 -translate-x-[70%] scale-90 rotate-[-5deg]' 
                            : 'opacity-60 z-20 translate-x-[70%] scale-90 rotate-[5deg]'}`}
            >
              <Image
                src="/images/demo/demo_portrait_original.png"
                alt="AI generated professional photo"
                fill
                className="object-cover"
                style={{ 
                  filter: 'brightness(1.05) contrast(1.05)',
                  animation: activeCard === 0 ? 'pulse 4s infinite' : 'none'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/10 to-blacbackgroundk/60 pointer-events-none" />
              <span className="absolute top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 py-1 rounded-full text-xs md:text-sm text-white font-medium shadow-lg">
                {t('labels.original')}
              </span>
            </div>

            {/* Casual Photo */}
            <div 
              className={`absolute w-[200px] h-[280px] sm:w-[220px] sm:h-[300px] md:w-[240px] md:h-[320px] lg:w-[260px] lg:h-[350px] 
                        transition-all duration-700 ease-in-out rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden
                        ${activeCard === 1 
                          ? 'opacity-100 z-30 translate-x-0 scale-100' 
                          : activeCard === 0 
                            ? 'opacity-60 z-20 translate-x-[70%] scale-90 rotate-[5deg]' 
                            : 'opacity-60 z-20 -translate-x-[70%] scale-90 rotate-[-5deg]'}`}
            >
              <Image
                src="/images/demo/demo_portrait_casual.png"
                alt="AI generated casual photo"
                fill
                className="object-cover"
                style={{ 
                  filter: 'brightness(1.02) contrast(1.02)',
                  animation: activeCard === 1 ? 'pulse 4s infinite' : 'none'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/10 to-background/60 pointer-events-none" />
              <span className="absolute top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 py-1 rounded-full text-xs md:text-sm text-white font-medium shadow-lg">
                {t('labels.casual')}
              </span>
            </div>

            {/* Fashion Photo */}
            <div 
              className={`absolute w-[200px] h-[280px] sm:w-[220px] sm:h-[300px] md:w-[240px] md:h-[320px] lg:w-[260px] lg:h-[350px] 
                        transition-all duration-700 ease-in-out rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden
                        ${activeCard === 2 
                          ? 'opacity-100 z-30 translate-x-0 scale-100' 
                          : activeCard === 1 
                            ? 'opacity-60 z-20 translate-x-[70%] scale-90 rotate-[5deg]' 
                            : 'opacity-60 z-20 -translate-x-[70%] scale-90 rotate-[-5deg]'}`}
            >
              <Image
                src="/images/demo/demo_portrait_fashion.png"
                alt="AI generated fashion photo"
                fill
                className="object-cover"
                style={{ 
                  filter: 'brightness(1.05) contrast(1.05)',
                  animation: activeCard === 2 ? 'pulse 4s infinite' : 'none'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/10 to-background/60 pointer-events-none" />
              <span className="absolute top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 py-1 rounded-full text-xs md:text-sm text-white font-medium shadow-lg">
                {t('labels.fashion')}
              </span>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-[15%] right-[25%] w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 blur-xl opacity-60" />
          <div className="absolute bottom-[20%] left-[15%] w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 blur-xl opacity-50" />
          <div className="absolute top-[40%] left-[20%] w-8 h-8 rounded-full bg-gradient-to-r from-pink-400 to-red-500 blur-xl opacity-40" />
        </div>
        
        {/* Right side - Content */}
        <div className="lg:w-1/2 text-left lg:mt-0">
          <div className="inline-flex items-center gap-2 px-2 py-1 md:pe-3 md:py-1 bg-violet-500/20 backdrop-blur-sm rounded-full mb-3 md:mb-4">
            <span className="bg-pink-500 text-white text-[10px] md:text-xs font-semibold px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-full">{t('new')}</span>
            <span className="text-violet-300 text-sm font-medium">{t('badge')}</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-6 leading-tight">
            {t('heading')}
          </h2>
          
          <p className="text-sm md:text-base lg:text-lg text-white/70 mb-5 md:mb-8 max-w-lg">
            {t('description')}
          </p>
          <Link 
            href="/try" 
            className="mt-6 px-8 py-2 bg-white/20 text-white rounded-full text-lg hover:bg-white/30 flex items-center gap-2 hover:scale-102 transform transition-all duration-200 w-fit cursor-pointer"
          >
            {t('cta')}
            <MoveUpRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}; 